import Link from "next/link"
import { memo } from "react"
import Badge from "../ui/Badge"
import CategoryChip from "../ui/CategoryChip"
import Meta from "../ui/Meta"
import CustomImage from "../ui/CustomImage"

import { useAuth } from "@/contexts/AuthContext"

const CHIP_LIMIT = 2

function parseCategoryIds(value) {
    return String(value || "")
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean)
}

/** Selected first only if it is actually assigned on the news. */
function orderCategoryIds(existingIds, selectedId) {
    const ordered = []
    const seen = new Set()

    if (selectedId && existingIds.includes(selectedId)) {
        ordered.push(selectedId)
        seen.add(selectedId)
    }

    for (const id of existingIds) {
        if (seen.has(id)) continue
        seen.add(id)
        ordered.push(id)
    }

    return ordered
}

function parseNewsCategoryIds(data) {
    const ids = parseCategoryIds(data?.categoryIds)
    if (data?.categoryId != null && data.categoryId !== '') {
        const primary = String(data.categoryId)
        if (!ids.includes(primary)) ids.unshift(primary)
    }
    return ids
}

function resolveChip(id, selectedCategory, categories) {
    const selectedId = selectedCategory?.id != null ? String(selectedCategory.id) : null
    const isSelected = selectedId && id === selectedId

    if (isSelected) {
        const name = selectedCategory.name || ""
        const slug =
            selectedCategory.slug ||
            selectedCategory.nameEnglish?.toLowerCase() ||
            ""
        return name && slug ? { name, slug } : null
    }

    const category = categories.find((cat) => String(cat.id) === String(id))
    if (!category?.name) return null

    const slug = category.slug || category.nameEnglish?.toLowerCase() || ""
    if (!slug) return null

    return { name: category.name, slug }
}

const StandardCard = ({
    selectedCategory = null,
    headline,
    layout = "row",
    badge,
    imageUrl,
    data = null,
}) => {
    const { categories } = useAuth()
    const isCol = layout === "col"

    const selectedId = selectedCategory?.id != null ? String(selectedCategory.id) : null
    const categoryIds = orderCategoryIds(parseNewsCategoryIds(data), selectedId).slice(0, CHIP_LIMIT)

    return (
        <article className={`w-full group ${isCol ? "news-card" : ""} ${layout === "row" ? "grid grid-cols-[96px_1fr] sm:grid-cols-[112px_1fr] gap-3.5 items-start" : "flex flex-col"}`}>
            <div className={`relative overflow-hidden shrink-0 ${isCol ? "rounded-t-[0.625rem]" : "rounded-md"}`}>
                <Link href={`/news/${data?.slug}`} className="block">
                    <CustomImage
                        src={imageUrl}
                        width={600}
                        height={layout === "row" ? 96 : 185}
                        alt={headline}
                        className={`w-full object-cover transition-transform duration-300 group-hover:scale-[1.03] ${layout === "row" ? "h-24 sm:h-28" : "h-[185px]"}`}
                    />

                    {badge && <Badge type={badge} />}
                </Link>
            </div>

            <div className={layout === "row" ? "min-w-0" : "p-3.5 pt-2.5"}>
                <div className="flex flex-wrap gap-2">
                    {categoryIds.map((id) => {
                        const chip = resolveChip(id, selectedCategory, categories)
                        if (!chip) return null

                        return (
                            <CategoryChip
                                key={id}
                                name={chip.name}
                                url={`/category/${chip.slug}`}
                            />
                        )
                    })}
                </div>

                <Link href={`/news/${data?.slug}`}>
                    <h4 className="mr mt-1.5 mb-1 font-semibold text-[15px] leading-snug text-(--text-primary) line-clamp-3 group-hover:text-(--brand-primary) transition-colors">
                        {headline}
                    </h4>
                </Link>
                <Meta compact data={data} />
            </div>
        </article>
    )
}

export default memo(StandardCard)
